import { useState, useEffect } from "react";
import { Save, Phone, Mail, User } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { authApi } from "@/utils/api";

const ProfileModal = ({ open, onOpenChange, user, onProfileUpdate }) => {
  const [profileForm, setProfileForm] = useState({
    name: "",
    last_name: "",
    phone: "",
    email: ""
  });
  const [savingProfile, setSavingProfile] = useState(false);

  // Update form when user data changes or modal opens
  useEffect(() => {
    if (user && open) {
      setProfileForm({
        name: user.name || "",
        last_name: user.last_name || "",
        phone: user.phone || "",
        email: user.email || ""
      });
    }
  }, [user, open]);

  const handleSaveProfile = async (e) => {
    e.preventDefault();
    setSavingProfile(true);
    
    try {
      const res = await authApi.updateProfile(profileForm);
      localStorage.setItem("admin_user", JSON.stringify(res.data));
      toast.success("Perfil actualizado exitosamente");
      onProfileUpdate(res.data);
      onOpenChange(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al actualizar perfil");
    } finally {
      setSavingProfile(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#121212] border-white/10 text-white max-w-md">
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2">
            <User className="text-[#D4AF37]" size={20} />
            Editar Mi Perfil
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSaveProfile} className="space-y-4 mt-2">
          <div className="space-y-2">
            <Label className="text-white">Nombre *</Label>
            <div className="relative">
              <User className="absolute left-3 top-1/2 -translate-y-1/2 text-[#A3A3A3]" size={16} />
              <Input 
                value={profileForm.name} 
                onChange={(e) => setProfileForm(p => ({...p, name: e.target.value}))} 
                className="pl-10 bg-[#1a1a1a] border-white/10 text-white" 
                placeholder="Tu nombre" 
                required 
                data-testid="profile-name-input"
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label className="text-white">Apellido</Label>
            <div className="relative">
              <User className="absolute left-3 top-1/2 -translate-y-1/2 text-[#A3A3A3]" size={16} />
              <Input 
                value={profileForm.last_name} 
                onChange={(e) => setProfileForm(p => ({...p, last_name: e.target.value}))} 
                className="pl-10 bg-[#1a1a1a] border-white/10 text-white" 
                placeholder="Tu apellido" 
                data-testid="profile-lastname-input"
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label className="text-white">Celular</Label>
            <div className="relative">
              <Phone className="absolute left-3 top-1/2 -translate-y-1/2 text-[#A3A3A3]" size={16} />
              <Input 
                value={profileForm.phone} 
                onChange={(e) => setProfileForm(p => ({...p, phone: e.target.value}))} 
                className="pl-10 bg-[#1a1a1a] border-white/10 text-white" 
                placeholder="+502 1234-5678" 
                data-testid="profile-phone-input"
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label className="text-white">Correo Electrónico *</Label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-[#A3A3A3]" size={16} />
              <Input 
                type="email"
                value={profileForm.email} 
                onChange={(e) => setProfileForm(p => ({...p, email: e.target.value}))} 
                className="pl-10 bg-[#1a1a1a] border-white/10 text-white" 
                placeholder="correo@ejemplo.com" 
                required
                data-testid="profile-email-input"
              />
            </div>
          </div>
          <div className="flex justify-end gap-3 pt-4">
            <Button 
              type="button" 
              variant="outline" 
              onClick={() => onOpenChange(false)} 
              className="border-white/10 text-white"
            >
              Cancelar
            </Button>
            <Button 
              type="submit" 
              className="bg-[#D4AF37] text-black hover:bg-[#FFE082]" 
              disabled={savingProfile}
              data-testid="save-profile-btn"
            >
              {savingProfile ? "Guardando..." : <><Save size={16} className="mr-2" />Guardar</>}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default ProfileModal;
